import { configureStore } from "@reduxjs/toolkit";
import { enableMapSet } from "immer";
import { useDispatch, useSelector } from "react-redux";
import type { TypedUseSelectorHook } from "react-redux";
import buyerReducer from "./slices/buyerSlice";
import supplierReducer from "./slices/supplierSlice";
import saleReducer from "./slices/saleSlice";
import proformaReducer from "./slices/proformaSlice";
import quotationReducer from "./slices/quotationSlice";
import labourReducer from "./slices/labourSlice";
import challanReducer from "./slices/challanSlice";
import productReducer from "./slices/productSlice";
import doctorReducer from "./slices/doctorSlice";
import manufacturingJournalReducer from "./slices/manufacturingJournalSlice";
import salesReceiptReducer from "./slices/salesReceiptSlice";

// Enable Immer support for Map and Set types used in slices
enableMapSet();

/**
 * Redux Store Configuration
 * Follows Redux Toolkit best practices with enhanced middleware and debugging
 */
export const store = configureStore({
  reducer: {
    buyer: buyerReducer,
    supplier: supplierReducer,
    sale: saleReducer,
    proforma: proformaReducer,
    quotation: quotationReducer,
    labour: labourReducer,
    challan: challanReducer,
    product: productReducer,
    doctor: doctorReducer,
    manufacturingJournal: manufacturingJournalReducer,
    salesReceipt: salesReceiptReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore all cache Maps and AbortControllers across all slices
        ignoredPaths: [
          "buyer.cache",
          "buyer.abortController",
          "buyer.requestAbortController",
          "supplier.cache",
          "supplier.abortController",
          "supplier.requestAbortController",
          "sale.cache",
          "sale.abortController",
          "sale.requestAbortController",
          "proforma.cache",
          "proforma.abortController",
          "proforma.requestAbortController",
          "quotation.cache",
          "quotation.abortController",
          "quotation.requestAbortController",
          "labour.cache",
          "labour.abortController",
          "labour.requestAbortController",
          "challan.cache",
          "challan.abortController",
          "challan.requestAbortController",
          "product.cache",
          "doctor.cache",
          "manufacturingJournal.cache",
          "salesReceipt.cache",
          "salesReceipt.abortController",
          "salesReceipt.requestAbortController",
        ],
      },
    }).concat(),
  devTools: process.env.NODE_ENV !== "production",
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Export typed hooks for use in components
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
